import { track } from './track'

// Post-deploy, a stale tab can request an old chunk hash that no longer exists.
// CF Pages now 404s those (see public/_redirects), so the dynamic import fails.
// We report both the failed <script>/<link> resource loads and the rejected
// dynamic imports to Analytics Engine (/_t/event) — the sink actually watched
// here — so recurrences are visible without opening Sentry.

const DYNAMIC_IMPORT_RE =
  /dynamically imported module|importing a module script failed|error loading|chunk|unexpected token '<'/i

// Cap reports per page load so a flapping resource can't drain the telemetry
// rate limit (TRACK_RL allows a limited burst).
let reported = 0
const MAX_REPORTS = 5

function report(kind: string, extra: Record<string, unknown>): void {
  if (reported >= MAX_REPORTS) return
  reported += 1
  track('error', { kind, ...extra })
}

export function installChunkErrorReporter(): void {
  if (typeof window === 'undefined') return

  // Resource load failures (bad <script src> / <link href>) don't bubble, so
  // listen in the capture phase. event.target is the failing element.
  window.addEventListener(
    'error',
    (event) => {
      const target = event.target as HTMLElement | null
      if (!target || target === (window as unknown as EventTarget)) return
      const tag = target.tagName
      if (tag !== 'SCRIPT' && tag !== 'LINK') return
      const url =
        (target as HTMLScriptElement).src ||
        (target as HTMLLinkElement).href ||
        ''
      if (!url) return
      report('resource_load', { tag: tag.toLowerCase(), url })
    },
    true,
  )

  // Rejected dynamic import() — the failure mode behind a dead lazy route.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message =
      typeof reason === 'string'
        ? reason
        : reason instanceof Error
          ? reason.message
          : ''
    if (!message || !DYNAMIC_IMPORT_RE.test(message)) return
    report('dynamic_import', { message: message.slice(0, 200) })
  })
}
